export enum JeopardyStates {
  WaitingForPlayers = 'waitingForPlayers',
  SelectingGame = 'selectingGame',
  CategorySelection = 'categorySelection',
  WaitingForBuzz = 'waitingForBuzz',
  PlayerAnswering = 'playerAnswering',
  CorrectAnswer = 'correctAnswer',
  DailyDoubleWager = 'dailyDoubleWager',
  ChallengeVoting = 'challengeVoting',
  RoundEnd = 'roundEnd',
  FinalJeopardy = 'finalJeopardy',
  CollectFinalJeopardyWagers = 'collectFinalJeopardyWagers',
  MakeFinalJeopardyGuesses = 'makeFinalJeopardyGuesses',
  RevealFinalJeopardyAnswer = 'revealFinalJeopardyAnswer',
  GameOver = 'gameOver',
}

export enum EventTypes {
  START_GAME = 'START_GAME',
  SAVE_NAME_DRAWING = 'SAVE_NAME_DRAWING',
  SELECT_GAME = 'SELECT_GAME',
  SELECT_CATEGORY = 'SELECT_CATEGORY',
  REVEAL_CLUE = 'REVEAL_CLUE',
  BUZZ_IN = 'BUZZ_IN',
  SUBMIT_WAGER = 'SUBMIT_WAGER',
  SUBMIT_ANSWER = 'SUBMIT_ANSWER',
  EVALUATE_ANSWER = 'EVALUATE_ANSWER',
  NEXT_CLUE = 'NEXT_CLUE',
  END_ROUND = 'END_ROUND',
  START_FINAL_JEOPARDY = 'START_FINAL_JEOPARDY',
  END_GAME = 'END_GAME',
  TIME_UP = 'TIME_UP',
  BUZZED_IN_TIME_UP = 'BUZZED_IN_TIME_UP',
  RESTART_GAME = 'RESTART_GAME',
  ADD_PLAYER = 'ADD_PLAYER',
  REMOVE_PLAYER = 'REMOVE_PLAYER',
  PING = 'PING',
  SET_PLAYER_TOPIC = 'SET_PLAYER_TOPIC',
  VOTE_CATEGORY = 'VOTE_CATEGORY',
  UNVOTE_CATEGORY = 'UNVOTE_CATEGORY',
  VET_CATEGORY = 'VET_CATEGORY',
  REJECT_CATEGORY = 'REJECT_CATEGORY',
  INCREMENT_ANSWERED_COUNT = 'INCREMENT_ANSWERED_COUNT',
  SET_QUESTION_AUDIO_PLAYING = 'SET_QUESTION_AUDIO_PLAYING',
  INITIATE_CHALLENGE = 'INITIATE_CHALLENGE',
  VOTE_CHALLENGE = 'VOTE_CHALLENGE',
  RESOLVE_CHALLENGE = 'RESOLVE_CHALLENGE',
  SKIP_TO_DOUBLE_JEOPARDY = 'SKIP_TO_DOUBLE_JEOPARDY',
  SKIP_TO_FINAL_JEOPARDY = 'SKIP_TO_FINAL_JEOPARDY',
  SET_MAX_PLAYERS = 'SET_MAX_PLAYERS',
  SET_AUDIENCE_ONLY = 'SET_AUDIENCE_ONLY',
  SET_FAIR_BUZZING = 'SET_FAIR_BUZZING',
}

export type Player = {
  name: string;
  score: number;
  nameSvg?: string;
  customTopic?: string;
  finalJeopardyWager: number | null;
  finalJeopardyGuess: string | null;
  finalJeopardyResult: boolean | null;
  hasBuzzed: boolean;
};

export type SubmittedCategory = {
  id: string; // Unique ID for this submission
  submitterName: string; // Player who submitted this category
  topic: string;
  votes: string[]; // Array of player names who voted
  status: "pending" | "generating" | "ready" | "error";
  vetted?: boolean; // Whether the host has approved this category for voting
  category?: Category; // Generated category once ready
};

export type PlayerAudio = {
  controlsBoard?: string[];      // Array of board control phrase variations
  correctFeedback?: string[];    // Array of correct answer phrase variations
  incorrectFeedback?: string[];  // Array of incorrect answer phrase variations
};

export type AnswerEvaluation = {
  clueId: string;
  categoryName: string;
  question: string;
  correctAnswer: string;
  playerName: string;
  playerAnswer: string;
  isCorrect: boolean;
  reason: string;
  timestamp: number;
};

export type Challenge = {
  challengerId: string;           // Player who initiated the challenge
  answerEvaluation: AnswerEvaluation; // The disputed answer
  clueValue: number;              // Points at stake
  votes: { [playerName: string]: boolean }; // true = overturn, false = uphold
  status: "pending" | "voting" | "resolved";
  result?: "overturned" | "upheld";
};

export type Clue = {
  id: string;
  question: string;
  answer: string;
  value: number;
  categoryId: string;
  isDailyDouble: boolean;
  answered: boolean;
  questionAudioUrl?: string; // base64 data URL for pre-generated question audio
  answerAudioUrl?: string; // base64 data URL for pre-generated answer audio
};

export type Category = {
  id: string;
  name: string;
  clues: Clue[];
  roundType: Rounds;
};

export enum Rounds {
  Jeopardy = 'jeopardy',
  DoubleJeopardy = 'doubleJeopardy',
  FinalJeopardy = 'finalJeopardy',
}

export type GameState = {
  gameId: string;
  currentState: JeopardyStates;
  maxPlayers?: number | null;
  audienceOnly?: boolean;
  players: Player[];
  categories: Category[];
  playerCategories?: {
    [playerName: string]: {
      status: "generating" | "ready" | "error";
      category?: Category;
      topic: string;
    }
  };
  submittedCategories?: { [categoryId: string]: SubmittedCategory }; // Player-submitted categories for voting (keyed by unique ID)
  playerAudio?: { [playerName: string]: PlayerAudio }; // Pre-generated personalized audio
  categoryAudioUrls?: { [categoryId: string]: string }; // Pre-generated category name audio
  answeredClueCount?: number;
  currentRound: Rounds;
  activePlayer: Player | null;
  activeClue: Clue | null;
  buzzedInPlayer: Player | null;
  dailyDoubleWager: number | null;
  lastAnsweredClueId: string | null;
  enableTTS?: boolean;
  questionAudioPlaying?: boolean; // True while question is being read aloud
  ttsProgress?: {
    current: number;
    total: number;
    message?: string;
    jeopardyRoundComplete?: boolean;
    doubleJeopardyRoundComplete?: boolean;
  };
  playerAudioProgress?: {
    [playerName: string]: {
      current: number;
      total: number;
      status: 'generating' | 'complete' | 'error';
    };
  };
  answerHistory?: AnswerEvaluation[]; // History of all answer evaluations
  activeChallenge?: Challenge; // Currently active challenge vote
  challengesUsedThisRound?: { [playerName: string]: boolean }; // Track challenges used per round
  fairBuzzingEnabled?: boolean; // When true, players must take turns buzzing
  fairBuzzingUsedThisCycle?: { [playerName: string]: boolean }; // Track who has buzzed in current fair buzzing cycle
};

export type GameEvent =
  | { type: EventTypes.PING }
  | { type: EventTypes.START_GAME }
  | { type: EventTypes.SELECT_GAME; gameId: string }
  | { type: EventTypes.SELECT_CATEGORY; categoryId: string; clueId: string }
  | { type: EventTypes.REVEAL_CLUE }
  | { type: EventTypes.BUZZ_IN; playerName: string; buzzInTime: number }
  | { type: EventTypes.SUBMIT_ANSWER; playerName: string; answer: string }
  | { type: EventTypes.EVALUATE_ANSWER; isCorrect: boolean }
  | { type: EventTypes.NEXT_CLUE }
  | { type: EventTypes.END_ROUND }
  | { type: EventTypes.START_FINAL_JEOPARDY }
  | { type: EventTypes.END_GAME }
  | { type: EventTypes.TIME_UP }
  | { type: EventTypes.BUZZED_IN_TIME_UP }
  | { type: EventTypes.SUBMIT_WAGER; wager: number; playerName: string }
  | { type: EventTypes.RESTART_GAME }
  | { type: EventTypes.ADD_PLAYER; name: string }
  | { type: EventTypes.REMOVE_PLAYER; playerName: string }
  | { type: EventTypes.SAVE_NAME_DRAWING; playerName: string; nameSvg: string }
  | { type: EventTypes.SET_PLAYER_TOPIC; playerName: string; topic: string }
  | { type: EventTypes.VOTE_CATEGORY; voterName: string; categoryId: string }
  | { type: EventTypes.UNVOTE_CATEGORY; voterName: string; categoryId: string }
  | { type: EventTypes.VET_CATEGORY; categoryId: string }
  | { type: EventTypes.REJECT_CATEGORY; categoryId: string }
  | { type: EventTypes.INCREMENT_ANSWERED_COUNT }
  | { type: EventTypes.SET_QUESTION_AUDIO_PLAYING; playing: boolean }
  | { type: EventTypes.INITIATE_CHALLENGE; playerName: string }
  | { type: EventTypes.VOTE_CHALLENGE; voterName: string; vote: boolean }
  | { type: EventTypes.RESOLVE_CHALLENGE }
  | { type: EventTypes.SKIP_TO_DOUBLE_JEOPARDY }
  | { type: EventTypes.SKIP_TO_FINAL_JEOPARDY }
  | { type: EventTypes.SET_MAX_PLAYERS; maxPlayers: number | null }
  | { type: EventTypes.SET_AUDIENCE_ONLY; audienceOnly: boolean }
  | { type: EventTypes.SET_FAIR_BUZZING; enabled: boolean };
